// src/layouts/CustomMenu.tsx
import React from 'react';
import { useTranslate, useSidebarState, useGetIdentity } from 'react-admin';
import { Box, useMediaQuery, MenuItem as MuiMenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';

// Iconos y logo
import { DashboardRounded as DashboardIcon } from '@mui/icons-material';
import { getMenuItems } from '../config/resourceConfig'; // Ajusta la ruta segÃºn tu estructura
import logo from '../assets/images/roommatik_logo.webp'; // Logo incluido

interface StyledMenuItemProps {
  open: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
    shouldForwardProp: (prop) => prop !== 'open',
})<StyledMenuItemProps>(({ theme, open }) => ({
    margin: theme.custom.menu.item.margin,
    padding: theme.custom.menu.item.padding,
    borderRadius: theme.custom.menu.item.borderRadius,
    color: theme.custom.menu.item.color(theme),
     '& .MuiListItemText-primary': {
        fontWeight: 600, 
    },
    
    display: 'flex',
    gap: theme.custom.menu.item.gap,
    transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
    
    '& .MuiListItemIcon-root': {
        minWidth: 'auto',
        color: 'inherit',

    },

    '&:hover:not(.active)': {
        color: theme.custom.menu.item.hover.color(theme),
        backgroundColor: open ? theme.custom.menu.item.hover.backgroundColor(theme) : 'transparent',
      
    },

    '&.active': {
        color: open ? theme.custom.menu.item.active.color(theme) : theme.palette.primary.main,
        backgroundColor: open ? theme.custom.menu.item.active.backgroundColor(theme) : 'transparent',
        fontWeight: theme.custom.menu.item.active.fontWeight,
        boxShadow: open ? theme.custom.menu.item.active.boxShadow(theme) : 'none',
        
        
        '& .MuiListItemIcon-root': {
            color: open ? 'inherit' : theme.custom.menu.item.closedActiveIcon.color(theme),
            backgroundColor: open ? 'transparent' : theme.custom.menu.item.closedActiveIcon.backgroundColor(theme),
            padding: open ? 0 : '4px',
            
        },

        '&:hover': {
            backgroundColor: open ? theme.custom.menu.item.active.hover.backgroundColor(theme) : 'transparent',
        },
    },
}));

export const CustomMenu: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate(); 
    const [isSidebarOpen, setSidebarOpen] = useSidebarState();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Obtener identidad del usuario para permisos
    const { data: identity, isLoading } = useGetIdentity();
    
    const getTranslationKey = (itemName: string): string => {
        const translationMap: Record<string, string> = {
            'lockers': 'resources.lockers.name',
            'users': 'resources.users.name', 
            'assignments': 'resources.locker-assignments.name',
            'hotels': 'resources.hotels.name',
            'zones': 'resources.zones.name',
            'groups': 'resources.groups.name',
            'machines': 'resources.machines.name',
            'reports': 'resources.reports.name',
            'settings': 'resources.settings.name',
           
            'products': 'resources.products.name',
        };
        return translationMap[itemName] || `resources.${itemName}.name`;
    };

    const dashboardItem = {
        name: 'dashboard',
        to: '/',
        icon: <DashboardIcon />,
        textKey: 'ra.page.dashboard' 
    };

    const menuItems = getMenuItems()
        .filter(item => item.name !== 'dashboard') 
        .map(item => ({
            name: item.name,
            to: item.route,
            icon: item.icon,
            textKey: getTranslationKey(item.name)
        }));


    const allItems = [dashboardItem, ...menuItems];

    const isActiveRoute = (to: string): boolean => location.pathname === to;

    const handleMenuClick = (to: string): void => { 
        navigate(to);
        if (isSmallScreen) {
            setSidebarOpen(false);
        }
    };


    if (isLoading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: isSmallScreen ? 'calc(100vh - 100px)' : 'calc(100vh - 100px)',
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                padding: '16px 12px',
                margin: isSmallScreen ? '3%' : '1%',
                borderRadius: theme.custom.radius.large,
                width: isSmallScreen ? '94%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                {/* Loading simple sin estropear el diseÃ±o */}
                <Box sx={{ color: theme.palette.text.secondary }}>
                    Cargando menÃº...
                </Box>
            </Box>
        );
    }


    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: isSmallScreen ? '100vh' : 'calc(100vh - 76px)',
            padding: '16px 12px',
            margin: isSmallScreen ? '3%' : '1%',
            borderRadius: theme.custom.radius.medium,
            width: isSmallScreen ? '94%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
            minWidth: isSmallScreen ? 'auto' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
            maxWidth: isSmallScreen ? '100%' : (isSidebarOpen ? theme.custom.menu.openWidth : theme.custom.menu.closedWidth),
            transition: 'width 0.2s ease-in-out, min-width 0.2s ease-in-out, max-width 0.2s ease-in-out',
        }}>
            <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {allItems.map(item => (
                    <StyledMenuItem
                        key={item.name}
                        onClick={() => handleMenuClick(item.to)} 
                        open={isSidebarOpen}
                        className={isActiveRoute(item.to) ? 'active' : ''}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText 
                            primary={translate(item.textKey, { smart_count: 1 })}
                            sx={{
                                transition: 'opacity 0.2s ease-in-out',
                                opacity: isSidebarOpen || isSmallScreen ? 1 : 0,
                            }}
                        />
                    </StyledMenuItem>
                ))}
            </Box>

            {(isSidebarOpen || isSmallScreen) && (
                <Box sx={{
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>

                    <img src={logo} alt="Logo" style={{ width: '180px', height: 'auto', marginBottom: '32px' }} />

                </Box>
            )}
        </Box>
    );
};

export default CustomMenu;
